import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, RotateCcw } from "lucide-react";

const Toggle = ({ on, onChange, label, desc, testid }) => (
  <label
    className="flex items-start gap-3 p-3 border cursor-pointer transition-colors"
    style={{
      borderColor: on ? "#9FA8D4" + "55" : "#2D2A26",
      backgroundColor: on ? "rgba(159,168,212,0.04)" : "transparent",
    }}
    data-testid={testid}
  >
    <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)}
           className="mt-0.5" />
    <div className="flex-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
        {label}
      </div>
      <div className="text-[11px] mt-1 text-[#A39E93] leading-snug">{desc}</div>
    </div>
    {on
      ? <Eye size={12} className="text-[#9FA8D4]" />
      : <EyeOff size={12} className="text-[#6F6A60]" />}
  </label>
);

const CardSettings = ({ card, onPatch }) => {
  const s = card?.settings || {};
  const [savingKey, setSavingKey] = useState(null);
  const [quote, setQuote] = useState(s.quote || "");
  const [favCat, setFavCat] = useState(card?.favorite_category || "");

  const set = async (key, value) => {
    try {
      setSavingKey(key);
      await onPatch({ [key]: value });
      toast.success(`${key.replace(/_/g, " ")} updated`);
    } catch {
      toast.error("Update failed");
    } finally { setSavingKey(null); }
  };

  const saveQuote = async () => {
    try {
      await onPatch({ quote });
      toast.success("Quote saved");
    } catch { toast.error("Could not save quote"); }
  };

  const saveFavorite = async () => {
    try {
      await onPatch({ favorite_category: favCat });
      toast.success(favCat ? "Favored domain set" : "Favored domain cleared (auto-derived)");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div data-testid="card-settings">
      <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#6F6A60] mb-3">
        // CARD SETTINGS · PRIVACY CONTROLS
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        <Toggle on={s.public_profile_enabled}
                onChange={(v) => set("public_profile_enabled", v)}
                testid="toggle-public-profile"
                label="PUBLIC PROFILE"
                desc="Allow /member/[callsign] to be visible to anyone with the link. Required for QR + share buttons. Default OFF." />
        <Toggle on={s.show_statistics}
                onChange={(v) => set("show_statistics", v)}
                testid="toggle-stats"
                label="SHOW STATISTICS"
                desc="Display read count, bookmarks, following count on the card." />
        <Toggle on={s.show_badges}
                onChange={(v) => set("show_badges", v)}
                testid="toggle-badges"
                label="SHOW BADGES"
                desc="Display earned commendations on the card." />
        <Toggle on={s.show_favorite_category}
                onChange={(v) => set("show_favorite_category", v)}
                testid="toggle-favorite-category"
                label="SHOW FAVORED DOMAIN"
                desc="Display your most-read category on the card." />
        <Toggle on={s.allow_qr_profile}
                onChange={(v) => set("allow_qr_profile", v)}
                testid="toggle-qr"
                label="QR PROFILE CODE"
                desc="Render a QR linking to your public profile (requires Public Profile)." />
      </div>

      {/* Quote */}
      <div className="border p-3 mb-3" style={{ borderColor: "#2D2A26" }}>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
          OPTIONAL QUOTE
        </label>
        <div className="flex gap-2 mt-2">
          <input
            type="text" value={quote} onChange={(e) => setQuote(e.target.value)}
            maxLength={120}
            placeholder="e.g. The truth leaves patterns."
            data-testid="input-quote"
            className="flex-1 bg-[#08070A] border px-2 py-1.5 text-sm text-[#E4DFD3] font-mono"
            style={{ borderColor: "#454038" }}
          />
          <button type="button" onClick={saveQuote}
                  data-testid="save-quote"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1.5"
                  style={{ borderColor: "#9FA8D4", color: "#9FA8D4" }}>
            SAVE
          </button>
        </div>
        <div className="text-[10px] text-[#6F6A60] mt-1.5">
          {120 - quote.length} characters remaining. Leave empty for an Archive default.
        </div>
      </div>

      {/* Favorite category override */}
      <div className="border p-3" style={{ borderColor: "#2D2A26" }}>
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
          FAVORED DOMAIN (manual override)
        </label>
        <div className="flex gap-2 mt-2">
          <input
            type="text" value={favCat} onChange={(e) => setFavCat(e.target.value)}
            maxLength={80}
            placeholder="Leave empty to auto-derive from reading history"
            data-testid="input-favorite-category"
            className="flex-1 bg-[#08070A] border px-2 py-1.5 text-sm text-[#E4DFD3] font-mono"
            style={{ borderColor: "#454038" }}
          />
          <button type="button" onClick={saveFavorite}
                  data-testid="save-favorite-category"
                  className="font-mono text-[10px] uppercase tracking-[0.3em] border px-3 py-1.5"
                  style={{ borderColor: "#9FA8D4", color: "#9FA8D4" }}>
            SAVE
          </button>
          {favCat && (
            <button type="button" onClick={() => { setFavCat(""); }}
                    title="Clear override (auto-derive)"
                    className="font-mono text-[10px] uppercase tracking-[0.3em] border px-2 py-1.5"
                    style={{ borderColor: "#454038", color: "#A39E93" }}>
              <RotateCcw size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardSettings;
